import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { ApplicationNeighbors, applicationsApi } from "api/applications";

const REMEMBERED_LIST_KEY = "application_neighbors_list";

// Storage is optional here: without it the page falls back to router state only.
const rememberedListQuery = (applicationId: string) => {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(REMEMBERED_LIST_KEY) ?? "null"
    ) as { applicationId?: string; listQuery?: string } | null;
    return saved?.applicationId === applicationId ? saved.listQuery : undefined;
  } catch {
    return undefined;
  }
};

const rememberListQuery = (applicationId: string, listQuery?: string) => {
  try {
    if (listQuery)
      sessionStorage.setItem(
        REMEMBERED_LIST_KEY,
        JSON.stringify({ applicationId, listQuery })
      );
    else sessionStorage.removeItem(REMEMBERED_LIST_KEY);
  } catch {
    // Same as above: nothing to remember with.
  }
};

/**
 * Position and previous/next ids of an application in the list it was opened
 * from (BSCPF-6998), plus `listQuery`: the list query those neighbors come
 * from, undefined for the default Applications list.
 *
 * The saved list is the applications list query (minus paging) in router
 * state, set by the list's row open and forwarded by next/previous; a page
 * opened any other way (link, search, notification) has none and uses the
 * default list. So does an application that is not in the saved list; the
 * walk then continues on the default list. A failed saved-list request stays
 * an error (the navigator hides) rather than moving the walk to another list.
 *
 * Fetched once per application and never refetched: after a decision moves #4
 * out of the Submitted tab, Next must still lead to the old #5.
 *
 * Moving around inside the application (into a report, a metric, a document
 * banner) navigates without router state, so the list in use is also kept for
 * this tab, for that one application, and read back when the state is missing.
 */
export const useApplicationNeighbors = (applicationId: string) => {
  const listQuery =
    (useLocation().state as { applicationsListQuery?: string } | null)
      ?.applicationsListQuery ?? rememberedListQuery(applicationId);

  const query = useQuery({
    queryKey: ["APPLICATION_NEIGHBORS", applicationId, listQuery],
    queryFn: async (): Promise<
      ApplicationNeighbors & { listQuery?: string }
    > => {
      if (listQuery) {
        const neighbors = await applicationsApi.getApplicationNeighbors(
          applicationId,
          listQuery
        );
        if (neighbors.position !== null) return { ...neighbors, listQuery };
      }

      return applicationsApi.getApplicationNeighbors(applicationId);
    },
    enabled: Boolean(applicationId),
    staleTime: Infinity
  });

  // Remember the list in use whenever this application shows it, cached or
  // fetched, so a later move into its reports finds it.
  useEffect(() => {
    if (query.data) rememberListQuery(applicationId, query.data.listQuery);
  }, [applicationId, query.data]);

  return query;
};
